/**
 * Order PDF Detail Drawer
 * Mobile-first in-app PDF preview with share action
 */

import { useTranslation, useIsRTL } from '@ufc/core'
import {
  AlertTriangle,
  Badge,
  BaseDrawer,
  Button,
  FileText,
  Loader2,
  Share,
  cn,
} from '@ufc/uikit'
import { PdfInlineViewer } from '../components/pdf/pdf-inline-viewer'

export type OrderPdfDocumentKind = 'order_confirmation' | 'delivery_note'

interface OrderPdfDetailDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  orderNumber: string
  documentKind?: OrderPdfDocumentKind
  fileName?: string
  fileSize?: string
  pdfUrl?: string
  pdfBlob?: Blob | null
  previewMode?: 'iframe' | 'pdfjs'
  isLoading?: boolean
  error?: string | null
  onShare?: () => void
  onRetry?: () => void
  onPreviewError?: (message?: string) => void
}

const HEADER_GRADIENT = 'from-slate-900 via-slate-800 to-slate-700'

function getDisplayValue(value: string | undefined, fallback: string): string {
  if (!value) return fallback
  const trimmed = value.trim()
  return trimmed === '' ? fallback : trimmed
}

function shouldShowViewer(params: {
  isLoading: boolean
  error: string | null
  pdfUrl?: string
  pdfBlob?: Blob | null
  previewMode: 'iframe' | 'pdfjs'
}): boolean {
  const { isLoading, error, pdfUrl, pdfBlob, previewMode } = params
  if (isLoading || error) return false
  if (previewMode === 'pdfjs') return !!pdfBlob
  return !!pdfUrl
}

function shouldShowPreviewUnavailable(
  isLoading: boolean,
  error: string | null,
  pdfUrl: string | undefined,
  previewMode: 'iframe' | 'pdfjs',
  pdfBlob?: Blob | null
): boolean {
  if (isLoading || error) return false
  if (previewMode === 'pdfjs') return !pdfBlob
  return !pdfUrl
}

function getViewerTitle(t: (key: string) => string, documentKind: OrderPdfDocumentKind): string {
  if (documentKind === 'delivery_note') {
    return t('orders.deliveryNotePreviewTitle')
  }
  return t('orders.pdfPreviewTitle')
}

function getViewerSubtitle(t: (key: string) => string, documentKind: OrderPdfDocumentKind): string {
  if (documentKind === 'delivery_note') {
    return t('orders.deliveryNotePreviewSubtitle')
  }
  return t('orders.pdfPreviewSubtitle')
}

function getFileNameLabel(t: (key: string) => string, documentKind: OrderPdfDocumentKind): string {
  if (documentKind === 'delivery_note') {
    return t('orders.deliveryNotePreviewDocumentName')
  }
  return t('orders.pdfPreviewFileName')
}

function getFileSizeLabel(t: (key: string) => string, documentKind: OrderPdfDocumentKind): string {
  if (documentKind === 'delivery_note') {
    return t('orders.deliveryNotePreviewDocumentSize')
  }
  return t('orders.pdfPreviewFileSize')
}

function getPdfBadgeLabel(t: (key: string) => string, documentKind: OrderPdfDocumentKind): string {
  if (documentKind === 'delivery_note') {
    return t('orders.deliveryNotePreviewBadge')
  }
  return t('orders.pdfPreviewBadge')
}

function getDismissLabel(t: (key: string) => string): string {
  return t('orders.pdfPreviewDismiss')
}

function getLoadingTitle(t: (key: string) => string, documentKind: OrderPdfDocumentKind): string {
  if (documentKind === 'delivery_note') {
    return t('orders.downloadingDeliveryNote')
  }
  return t('orders.downloadingPDF')
}

function getLoadingDescription(
  t: (key: string, options?: { orderNumber?: string }) => string,
  documentKind: OrderPdfDocumentKind,
  orderNumber: string,
): string {
  if (documentKind === 'delivery_note') {
    return t('orders.downloadingDeliveryNoteDesc', { orderNumber })
  }
  return t('orders.downloadingPDFDesc', { orderNumber })
}

function getErrorTitle(t: (key: string) => string, documentKind: OrderPdfDocumentKind): string {
  if (documentKind === 'delivery_note') {
    return t('orders.deliveryNoteDownloadError')
  }
  return t('orders.pdfDownloadError')
}

function getErrorFallbackDescription(
  t: (key: string) => string,
  documentKind: OrderPdfDocumentKind,
): string {
  if (documentKind === 'delivery_note') {
    return t('orders.deliveryNoteDownloadErrorDesc')
  }
  return t('orders.pdfDownloadErrorDesc')
}

function getShareLabel(t: (key: string) => string): string {
  return t('orders.pdfPreviewShare')
}

function getRetryLabel(t: (key: string) => string): string {
  return t('orders.pdfPreviewRetry')
}

function getPreviewUnavailableTitle(t: (key: string) => string): string {
  return t('orders.pdfPreviewUnavailableTitle')
}

function getPreviewUnavailableDesc(t: (key: string) => string): string {
  return t('orders.pdfPreviewUnavailableDesc')
}

function isShareDisabled(params: {
  onShare?: () => void
  isLoading: boolean
  error: string | null
  pdfUrl?: string
  pdfBlob?: Blob | null
}): boolean {
  const { onShare, isLoading, error, pdfUrl, pdfBlob } = params
  if (!onShare) return true
  if (isLoading || !!error) return true
  return !pdfBlob && !pdfUrl
}

export function OrderPdfDetailDrawer({
  open,
  onOpenChange,
  orderNumber,
  documentKind = 'order_confirmation',
  fileName,
  fileSize,
  pdfUrl,
  pdfBlob,
  previewMode = 'iframe',
  isLoading = false,
  error = null,
  onShare,
  onRetry,
  onPreviewError,
}: OrderPdfDetailDrawerProps) {
  const { t } = useTranslation()
  const isRTL = useIsRTL()
  const orderDisplay = getDisplayValue(orderNumber, t('common.notAvailable'))

  const headerContent = (
    <div className={cn('flex items-center gap-4', isRTL && 'flex-row-reverse')}>
      <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
        <FileText className="w-6 h-6 text-white" />
      </div>
      <div className={cn('flex flex-col gap-1', isRTL && 'text-right')}>
        <span className="text-white/70 text-sm">{getViewerTitle(t, documentKind)}</span>
        <h2 className="text-xl font-bold text-white">{orderDisplay}</h2>
        <span className="text-white/60 text-xs">{getViewerSubtitle(t, documentKind)}</span>
      </div>
    </div>
  )

  const topBarContent = (
    <div className={cn('flex flex-wrap items-center justify-between gap-4 w-full', isRTL && 'flex-row-reverse')}>
      <div className={cn('flex items-center gap-3', isRTL && 'flex-row-reverse')}>
        <div className={cn('flex flex-col gap-1', isRTL && 'text-right')}>
          <span className="text-xs text-muted-foreground">{getFileNameLabel(t, documentKind)}</span>
          <span className="text-sm font-semibold text-foreground max-w-[220px] truncate">
            {getDisplayValue(fileName, t('common.notAvailable'))}
          </span>
        </div>
      </div>
      <div className={cn('flex items-center gap-2', isRTL && 'flex-row-reverse')}>
        <Badge className="bg-slate-900/10 text-slate-700 border border-slate-200">
          {getPdfBadgeLabel(t, documentKind)}
        </Badge>
        <span className="text-xs text-muted-foreground">
          {getFileSizeLabel(t, documentKind)}: {getDisplayValue(fileSize, t('common.notAvailable'))}
        </span>
      </div>
    </div>
  )

  const shareDisabled = isShareDisabled({
    onShare,
    isLoading,
    error,
    pdfUrl,
    pdfBlob,
  })

  const footerContent = (
    <div className={cn('flex gap-3 safe-bottom', isRTL && 'flex-row-reverse')}>
      <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
        {getDismissLabel(t)}
      </Button>
      <Button
        className="flex-1 gap-2"
        onClick={onShare}
        disabled={shareDisabled}
      >
        <Share className="w-4 h-4" />
        {getShareLabel(t)}
      </Button>
    </div>
  )

  const showViewer = shouldShowViewer({
    isLoading,
    error,
    pdfUrl,
    pdfBlob,
    previewMode,
  })
  const showPreviewUnavailable = shouldShowPreviewUnavailable(
    isLoading,
    error,
    pdfUrl,
    previewMode,
    pdfBlob
  )

  return (
    <BaseDrawer
      open={open}
      onOpenChange={onOpenChange}
      isRTL={isRTL}
      headerGradient={HEADER_GRADIENT}
      headerContent={headerContent}
      topBarContent={topBarContent}
      footerContent={footerContent}
      contentScrollable={false}
      contentClassName="p-0"
      fixedWidth={760}
      tabletWidth="95%"
      mobileWidth="100%"
      title={getViewerTitle(t, documentKind)}
    >
      <div className="flex h-full min-h-0 flex-col">
        {isLoading && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-slate-50 px-6 py-12 text-center">
            <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
            <div>
              <p className="text-sm font-semibold text-slate-700">{getLoadingTitle(t, documentKind)}</p>
              <p className="text-xs text-muted-foreground">
                {getLoadingDescription(t, documentKind, orderDisplay)}
              </p>
            </div>
          </div>
        )}

        {error && !isLoading && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-rose-50 px-6 py-10 text-center">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-rose-100">
              <AlertTriangle className="w-5 h-5 text-rose-500" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold text-rose-700">{getErrorTitle(t, documentKind)}</p>
              <p className="text-xs text-rose-600">
                {getDisplayValue(error, getErrorFallbackDescription(t, documentKind))}
              </p>
            </div>
            {onRetry && (
              <Button variant="outline" className="mt-2" onClick={onRetry}>
                {getRetryLabel(t)}
              </Button>
            )}
          </div>
        )}

        {showViewer && previewMode === 'iframe' && (
          <div className="flex-1 min-h-0 bg-white">
            <iframe
              title={getViewerTitle(t, documentKind)}
              src={pdfUrl}
              className="h-full w-full"
            />
          </div>
        )}

        {showViewer && previewMode === 'pdfjs' && (
          <div className="flex-1 min-h-0 overflow-auto bg-white">
            <PdfInlineViewer
              blob={pdfBlob}
              className="h-full"
              onError={onPreviewError}
            />
          </div>
        )}

        {showPreviewUnavailable && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-slate-50 px-6 py-10 text-center">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-200">
              <FileText className="w-5 h-5 text-slate-600" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold text-slate-700">{getPreviewUnavailableTitle(t)}</p>
              <p className="text-xs text-muted-foreground">{getPreviewUnavailableDesc(t)}</p>
            </div>
            {onShare && (
              <Button
                variant="outline"
                className="mt-2 gap-2"
                onClick={onShare}
                disabled={shareDisabled}
              >
                <Share className="w-4 h-4" />
                {getShareLabel(t)}
              </Button>
            )}
          </div>
        )}
      </div>
    </BaseDrawer>
  )
}
